import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const SocialMediaServices = () => {
  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      {/* Organic gradient on the right with animations */}
      <div className="absolute -top-20 -right-20 w-[800px] h-[800px]">
        <div 
          className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-br from-purple-500/40 via-indigo-500/30 to-blue-500/20 rounded-full blur-3xl"
          style={{
            animation: 'floatSlow 10s ease-in-out infinite'
          }}
        ></div>
        <div 
          className="absolute top-60 right-40 w-[500px] h-[500px] bg-gradient-to-tl from-indigo-400/30 via-purple-400/20 to-violet-400/15 rounded-full blur-2xl"
          style={{
            animation: 'driftRight 12s ease-in-out infinite'
          }}
        ></div>
      </div>

      {/* Organic gradient on the bottom left with animations */}
      <div className="absolute -bottom-40 -left-40 w-[900px] h-[900px]">
        <div 
          className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-gradient-to-br from-indigo-500/35 via-purple-500/25 to-violet-500/20 rounded-full blur-3xl"
          style={{
            animation: 'floatLeft 14s ease-in-out infinite'
          }}
        ></div>
        <div 
          className="absolute bottom-40 left-40 w-[600px] h-[600px] bg-gradient-to-tl from-purple-400/30 via-indigo-400/20 to-blue-400/15 rounded-full blur-2xl"
          style={{
            animation: 'driftLeft 8s ease-in-out infinite'
          }}
        ></div>
      </div>

      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-32 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-left">
            <div className="mb-8">
              <span className="text-white text-sm font-semibold tracking-wider uppercase opacity-90">FOCUS AREAS</span>
            </div>
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-light text-white leading-tight">
              Social Media Presence Management
            </h1>
            <p className="text-xl text-gray-300 mt-8 max-w-3xl font-light leading-relaxed">
              In today's digital world, your online presence defines your brand. We create and manage engaging, professional content across key platforms.
            </p>
          </div>
        </div>
      </section>

      {/* Services Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left column - Header */}
            <div>
              <h2 className="text-3xl md:text-5xl font-bold text-white font-AfacadFluxLight leading-tight mb-8">
                Comprehensive social media management for brand growth
              </h2>
            </div>
            {/* Right column - Content */}
            <div className="space-y-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                Our comprehensive social media presence management services are designed to elevate your brand across all major platforms. We provide <span className="text-purple-300 font-medium">multi-platform management</span>, ensuring consistent branding and regular updates across Instagram, TikTok, LinkedIn, and Facebook.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                From <span className="text-purple-300 font-medium">professional content creation</span> to strategic <span className="text-purple-300 font-medium">engagement optimization</span>, we handle the digital complexities so your team can concentrate on business growth while we build your online presence.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                With our expertise in corporate-level videos, creative content, and employee interviews, we ensure your brand stands out in the digital landscape and connects meaningfully with your audience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 relative z-10">
        <div className="max-w-7xl mx-auto">
            {/* Services Grid */}
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8 text-left">
            <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V3a2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2h1m12 0H5m7 4v6m-3-3h6" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">Multi-Platform Management</h3>
              <p className="text-gray-300 font-light">We manage your company's presence across all major social media platforms, ensuring consistent branding, regular updates, and audience engagement.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Instagram management
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  TikTok presence
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  LinkedIn professional content
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Facebook community building
                </li>
              </ul>
              </div>
            <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">Content Creation</h3>
              <p className="text-gray-300 font-light">We create engaging and professional content tailored to your business needs, showcasing your brand and connecting with audiences.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Corporate-level videos
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Fun & creative videos
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Employee interviews
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Brand achievement showcases
                </li>
              </ul>
              </div>
            <div className="bg-slate-800/30 p-8 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:bg-slate-800/50 transition-all duration-300">
                <div className="text-purple-300 mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              <h3 className="text-xl font-light text-white mb-3">High Reach & Engagement</h3>
              <p className="text-gray-300 font-light">Our strategies are designed to maximize visibility and interaction, increasing reach across platforms and boosting engagement with followers.</p>
              <ul className="mt-4 space-y-2 text-sm text-gray-300">
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Increasing reach across platforms
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Boosting engagement with followers
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Driving brand awareness
                </li>
                <li className="flex items-center">
                  <span className="text-purple-300 mr-2">•</span>
                  Audience growth strategies
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <div className="mb-4">
              <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">PLATFORMS WE MANAGE</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight max-w-4xl">
              We back founders from idea to seed - and beyond, goal after goal
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-16 lg:gap-20">
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-pink-500 to-orange-500 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">Instagram</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Visual storytelling and brand showcase</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-black to-gray-800 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">TikTok</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Short-form video content and viral trends</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">LinkedIn</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Professional networking and B2B marketing</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">Facebook</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Community building and brand awareness</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight max-w-4xl font-AfacadFluxLight">
              How it Works
            </h2>
          </div>
          
          <div className="relative">
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              centeredSlides={true}
              navigation={{
                nextEl: '.swiper-button-next-custom-social',
                prevEl: '.swiper-button-prev-custom-social'
              }}
              pagination={{
                clickable: true,
                el: '.swiper-pagination-custom-social'
              }}
              breakpoints={{
                640: { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 }
              }}
              className="h-auto"
            >
              <SwiperSlide>
                <div className="bg-slate-800/30 rounded-3xl p-8 h-full border border-slate-700/50 backdrop-blur-sm flex flex-col">
                  <div className="mb-6">
                    <span className="text-sm font-semibold tracking-widest uppercase text-gray-300">1. EXPLORE</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-purple-300 mb-6 leading-tight">
                    Find The Best Social Strategy
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-8 flex-grow">
                    We develop comprehensive social media strategy and content plans to unlock engagement opportunities across all major platforms.
                  </p>
                  <div className="flex justify-center mt-auto">
                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center border border-white/10">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="bg-slate-800/30 rounded-3xl p-8 h-full border border-slate-700/50 backdrop-blur-sm flex flex-col">
                  <div className="mb-6">
                    <span className="text-sm font-semibold tracking-widest uppercase text-gray-300">2. CREATE</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-purple-300 mb-6 leading-tight">
                    Turn Ideas Into Viral Content
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-8 flex-grow">
                    We create engaging, platform-specific content and graphics that resonate with your audience and drive meaningful engagement.
                  </p>
                  <div className="flex justify-center mt-auto">
                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center border border-white/10">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V3a2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2h1m12 0H5m7 4v6m-3-3h6" />
                      </svg>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              <SwiperSlide>
                <div className="bg-slate-800/30 rounded-3xl p-8 h-full border border-slate-700/50 backdrop-blur-sm flex flex-col">
                  <div className="mb-6">
                    <span className="text-sm font-semibold tracking-widest uppercase text-gray-300">3. BUILD</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-light text-purple-300 mb-6 leading-tight">
                    Take Your Brand Into The Spotlight
                  </h3>
                  <p className="text-lg text-gray-300 leading-relaxed mb-8 flex-grow">
                    If you succeed, you'll have a thriving social media presence and we'll be your ongoing partner for community management and growth optimization.
                  </p>
                  <div className="flex justify-center mt-auto">
                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center border border-white/10">
                      <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>

            {/* Custom Navigation Buttons */}
            <div className="swiper-button-prev-custom-social absolute left-0 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
            <div className="swiper-button-next-custom-social absolute right-0 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Custom Pagination */}
            <div className="swiper-pagination-custom-social mt-8 flex justify-center"></div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <div className="mb-4">
              <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">WHY CHOOSE US</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight max-w-4xl">
              We back founders from idea to seed - and beyond, goal after goal
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-16 lg:gap-20">
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">Proven Results</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Track record of increasing engagement and growing audiences</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">Data-Driven</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Analytics and insights to optimize your social media strategy</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="text-purple-300">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl font-light text-white">24/7 Monitoring</h3>
                <p className="text-lg text-gray-300 leading-relaxed font-light">Round-the-clock monitoring and rapid response to engagement</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-AfacadFluxLight font-light text-white leading-tight mb-8">
            Ready to Build Your Social Media Presence?
          </h2>
          <p className="text-xl text-gray-300 mb-12 font-light">
            Let our social media experts help you connect with your audience and grow your brand
          </p>
          <div className="flex justify-center">
            <Link to="/contact" className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-10 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl font-AfacadFluxLight uppercase tracking-wide">
              Get Started Today
            </Link>
          </div>
        </div>
      </section>


      {/* CSS Animations */}
      <style jsx>{`
        @keyframes floatSlow {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(80px, -100px) rotate(3deg);
          }
          66% {
            transform: translate(-60px, 60px) rotate(-2deg);
          }
        }

        @keyframes driftRight {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          25% {
            transform: translate(40px, -60px) scale(1.1);
          }
          50% {
            transform: translate(-32px, 32px) scale(0.88);
          }
          75% {
            transform: translate(48px, 40px) scale(1.06);
          }
        }

        @keyframes floatLeft {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(-72px, 88px) rotate(-3deg);
          }
          66% {
            transform: translate(100px, -72px) rotate(2.5deg);
          }
        }

        @keyframes driftLeft {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          25% {
            transform: translate(-48px, 64px) scale(1.12);
          }
          50% {
            transform: translate(32px, -40px) scale(0.84);
          }
          75% {
            transform: translate(-56px, -32px) scale(1.08);
          }
        }
      `}</style>
    </div>
  );
};

export default SocialMediaServices; 